import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { v4 as uuidv4 } from 'uuid';
import { ALTERNATIVE_APP_URLS } from "src/constants/AppConstants";
import { PulseSurveyNotification as PulseSurveyNotificationType, PulseSurveyNotificationContainerProps, PulseSurveyNotificationDisplayLocation } from "./PulseSurveyNotificationTypes";
import { getDisplayEverywhereSurveyNotifications, getClosePortalSurveyNotifications, getEverywhereClosePortalSurveyNotifications, getUrlPrefixSurveyNotifications, getAppSurveyNotifications, getEverywhereAppSurveyNotifications, getTriggerSurveyNotifications } from "./PulseSurveyNotificationUtils";
import PulseSurveyNotification from "./PulseSurveyNotification";
import { createPreference, listPreference } from "src/utilities/AppSyncPlusAPIs";
import { clearPendingTrigger } from "src/store/pulseSurveyTriggerSlice";

const PULSE_SURVEY_VIEW_TRACKING_PREFERENCE_TYPE = "PULSE_SURVEY_AUTO_POPUP_VIEW";
const PULSE_SURVEY_VIEW_TRACKING_ASSOCIATION_TYPE = "PULSE_SURVEY_VIEW_USER";

export default (props: PulseSurveyNotificationContainerProps) => {
    const [surveyNotifications, setSurveyNotifications] = useState<PulseSurveyNotificationType[]>([]);
    const [triggerSurveyNotifications, setTriggerSurveyNotifications] = useState<PulseSurveyNotificationType[]>([]);
    const [dismissedSurveyNotifications, setDismissedSurveyNotifications] = useState<Set<string>>(new Set([]));
    const [viewCounts, setViewCounts] = useState<Record<string, number>>({});
    const [viewCountsLoaded, setViewCountsLoaded] = useState(false);
    const userId = useSelector((globalState: any) => globalState.auth.userId);
    const pendingTrigger = useSelector((globalState: any) => globalState.pulseSurveyTrigger?.pendingTrigger);
    const dispatch = useDispatch();
    const ln = useLocation();

    const filterDismissedSurveys = (surveyNotificationsToFilter: PulseSurveyNotificationType[]) => {
        return surveyNotificationsToFilter.filter(survey => !dismissedSurveyNotifications.has(survey.preferenceId));
    };

    const loadViewCounts = async () => {
        if (!userId) return;
        const associationId = `PULSE_SURVEY_VIEWS#${userId}`;
        return new Promise<void>((resolve) => {
            listPreference(
                associationId,
                PULSE_SURVEY_VIEW_TRACKING_ASSOCIATION_TYPE,
                PULSE_SURVEY_VIEW_TRACKING_PREFERENCE_TYPE,
                (response: any) => {
                    const items = response?.data?.listPreference?.items || [];
                    const counts: Record<string, number> = {};
                    for (const item of items) {
                        const info = typeof item.information === 'string' ? JSON.parse(item.information) : item.information;
                        const notifId = info?.notificationId;
                        if (notifId) {
                            counts[notifId] = (counts[notifId] || 0) + 1;
                        }
                    }
                    setViewCounts(counts);
                    setViewCountsLoaded(true);
                    resolve();
                },
                () => {
                    setViewCountsLoaded(true);
                    resolve();
                }
            );
        });
    };

    const recordAutoPopupView = (notificationId: string) => {
        if (!userId || !notificationId) return;
        const associationId = `PULSE_SURVEY_VIEWS#${userId}`;
        const newId = uuidv4();
        createPreference(
            newId,
            PULSE_SURVEY_VIEW_TRACKING_PREFERENCE_TYPE,
            `Auto popup view: ${notificationId}`,
            associationId,
            PULSE_SURVEY_VIEW_TRACKING_ASSOCIATION_TYPE,
            userId,
            { notificationId, viewedAt: new Date().toISOString() },
            null,
            () => {
                setViewCounts(prev => ({
                    ...prev,
                    [notificationId]: (prev[notificationId] || 0) + 1
                }));
            },
            () => {}
        );
    };

    const shouldAutoPopup = (surveyNotification: PulseSurveyNotificationType, isTrigger: boolean = false): boolean => {
        // Trigger notifications auto-popup by nature of being triggered
        if (!isTrigger && !surveyNotification.information.autoPopup) return false;
        const maxCount = surveyNotification.information.autoPopupMaxCount;
        if (!maxCount) return true; // unlimited
        const currentCount = viewCounts[surveyNotification.preferenceId] || 0;
        return currentCount < maxCount;
    };

    const loadSurveyNotificaitons = async () => {
        let getSurveyNotificationPromises: Promise<PulseSurveyNotificationType[]>[] = [];

        if (props.displayLocations.includes(PulseSurveyNotificationDisplayLocation.Everywhere)) {
            getSurveyNotificationPromises.push(getDisplayEverywhereSurveyNotifications());
        }

        if (props.displayLocations.includes(PulseSurveyNotificationDisplayLocation.ClosePortal)) {
            getSurveyNotificationPromises.push(getEverywhereClosePortalSurveyNotifications());
            if (props.parameters?.closePortalOwnerId) {
                getSurveyNotificationPromises.push(getClosePortalSurveyNotifications(props.parameters.closePortalOwnerId));
            }
        }

        if (props.displayLocations.includes(PulseSurveyNotificationDisplayLocation.UrlPrefix) && props.parameters?.urlPrefix) {
            getSurveyNotificationPromises.push(getUrlPrefixSurveyNotifications(props.parameters.urlPrefix.toLowerCase()));
        }

        if (props.displayLocations.includes(PulseSurveyNotificationDisplayLocation.App)
            && (
                ln.pathname.startsWith("/App/")
                || !!ALTERNATIVE_APP_URLS.find(url => ln.pathname.startsWith(url))
            )
        ) {
            getSurveyNotificationPromises.push(getEverywhereAppSurveyNotifications());
            if (props.parameters?.appId) {
                getSurveyNotificationPromises.push(getAppSurveyNotifications(props.parameters.appId));
            }
        }

        const surveyNotificationArrays = await Promise.all(getSurveyNotificationPromises);
        const surveyNotificationsToDisplay = surveyNotificationArrays.reduce((prev, surveyNotificationArray) => {
            return prev.concat(surveyNotificationArray);
        }, []);
        setSurveyNotifications(filterDismissedSurveys(surveyNotificationsToDisplay));
    };

    useEffect(() => {
        loadViewCounts();
    }, [userId]);

    useEffect(() => {
        loadSurveyNotificaitons();
    }, [ln.pathname, props.parameters?.urlPrefix, props.parameters?.appId, props.parameters?.closePortalOwnerId]);

    useEffect(() => {
        setSurveyNotifications(filterDismissedSurveys(surveyNotifications));
    }, [dismissedSurveyNotifications]);

    // Handle trigger-based survey notifications from Redux state
    useEffect(() => {
        if (pendingTrigger) {
            const loadTriggerNotifications = async () => {
                try {
                    const triggerNotifications = await getTriggerSurveyNotifications(pendingTrigger);
                    setTriggerSurveyNotifications(filterDismissedSurveys(triggerNotifications));
                } catch (e) {
                    console.error("Error loading trigger survey notifications:", e);
                }
                // Clear the pending trigger after processing
                dispatch(clearPendingTrigger());
            };
            loadTriggerNotifications();
        }
    }, [pendingTrigger]);

    return (
        <>
            {surveyNotifications.map((surveyNotification) => (
                <PulseSurveyNotification
                    key={surveyNotification.preferenceId}
                    surveyId={surveyNotification.information.surveyId}
                    notificationDisplayType={surveyNotification.information.notificationDisplayType.value}
                    notificationText={surveyNotification.information.notificationText}
                    autoPopup={viewCountsLoaded && shouldAutoPopup(surveyNotification)}
                    autoPopupMaxCount={surveyNotification.information.autoPopupMaxCount}
                    autoPopupDelayType={surveyNotification.information.autoPopupDelayType}
                    autoPopupDelaySeconds={surveyNotification.information.autoPopupDelaySeconds}
                    notificationId={surveyNotification.preferenceId}
                    userId={userId}
                    onAutoPopupShown={recordAutoPopupView}
                    options={{
                        roundedCorners: props?.roundedCorners,
                        onDismiss: surveyNotification.information.dismissible ? () => {
                            setDismissedSurveyNotifications(new Set([...Array.from(dismissedSurveyNotifications), surveyNotification.preferenceId]));
                        } : undefined
                    }}
                />
            ))}
            {triggerSurveyNotifications.map((surveyNotification) => (
                <PulseSurveyNotification
                    key={`trigger-${surveyNotification.preferenceId}`}
                    surveyId={surveyNotification.information.surveyId}
                    notificationDisplayType={surveyNotification.information.notificationDisplayType?.value}
                    notificationText={surveyNotification.information.notificationText}
                    autoPopup={viewCountsLoaded && shouldAutoPopup(surveyNotification, true)}
                    autoPopupMaxCount={surveyNotification.information.autoPopupMaxCount}
                    autoPopupDelayType={surveyNotification.information.autoPopupDelayType}
                    autoPopupDelaySeconds={surveyNotification.information.autoPopupDelaySeconds}
                    notificationId={surveyNotification.preferenceId}
                    userId={userId}
                    onAutoPopupShown={recordAutoPopupView}
                    hideBanner={true}
                    options={{
                        roundedCorners: props?.roundedCorners,
                        onDismiss: () => {
                            setTriggerSurveyNotifications(prev => prev.filter(n => n.preferenceId !== surveyNotification.preferenceId));
                        }
                    }}
                />
            ))}
        </>
    );
};
